import { roundToNearestMinutes, RoundToNearestMinutesOptions } from 'date-fns';
import { MarketKey } from 'pages/Optimisation/components/ContractedVolume/types';
import { WHOLESALE_MARKET_STACKING_ORDER } from 'pages/Optimisation/constants';
import { IBaseTrade, IChartTrade } from 'types/trade';
import { flattenEdges } from 'utils/graphql';

const roundConfig: RoundToNearestMinutesOptions = {
  nearestTo: 1,
  roundingMethod: 'round',
};

export const queryDataToD3Data = (
  key: MarketKey,
  data = {},
  reshapeDate: boolean = true,
): IChartTrade[] => {
  let d3Data: IChartTrade[] = flattenEdges(data, key + 'Trades').map(
    (dataPoint: IBaseTrade) => {
      const start = roundToNearestMinutes(
        new Date(dataPoint.start),
        roundConfig,
      );
      const end = roundToNearestMinutes(new Date(dataPoint.end), roundConfig);

      const negativePowerIfImport = (() => {
        if (key === MarketKey.Ancillary) {
          return dataPoint.market.endsWith('High') ||
            dataPoint.market.startsWith('negative')
            ? -1 // xHigh is import
            : 1;
        }

        return dataPoint.side === 'buy' ? -1 : 1;
      })();

      const value = dataPoint.power * negativePowerIfImport;

      return {
        id: dataPoint.id,
        tradeGroupSourceId: dataPoint.tradeGroupSourceId,
        start,
        end,
        value,
        market: dataPoint.market,
        side: dataPoint.side,
        price: dataPoint.price,
        key,
      };
    },
  );

  if (!d3Data || d3Data.length <= 0) {
    return [];
  }

  if (reshapeDate) {
    if (key === MarketKey.Wholesale || key === MarketKey.BalancingMechanism) {
      d3Data = reshapeAndSplitTrades(d3Data, key);
    }

    if (key === MarketKey.Wholesale) {
      type WholesaleMarket = keyof typeof WHOLESALE_MARKET_STACKING_ORDER;
      const sortOrder = (trade: IChartTrade) =>
        WHOLESALE_MARKET_STACKING_ORDER[trade.market as WholesaleMarket];
      d3Data.sort(
        (a: IChartTrade, b: IChartTrade) => sortOrder(a) - sortOrder(b),
      );
    }
  }
  return d3Data;
};

function calculateDurationInMinutes(start: Date, end: Date): number {
  return Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
}

const tradeFitsOneSettlementPeriod = (start: Date, end: Date): boolean => {
  const duration = calculateDurationInMinutes(start, end);

  const startMinutes = start.getMinutes();
  const endMinutes = end.getMinutes();

  const isStartInFirstHalf = startMinutes >= 0 && startMinutes <= 30;
  const isEndInFirstHalf = endMinutes >= 0 && endMinutes <= 30;
  const isStartInSecondHalf = startMinutes >= 30 && startMinutes < 60;
  const isEndInSecondHalf =
    endMinutes >= 30 && (endMinutes < 60 || endMinutes === 0);

  const fitsInFirstHalf = isStartInFirstHalf && isEndInFirstHalf;
  const fitsInSecondHalf = isStartInSecondHalf && isEndInSecondHalf;

  return duration <= 30 && (fitsInFirstHalf || fitsInSecondHalf);
};

function splitTrade(
  trade: IChartTrade,
  result: IChartTrade[],
  marketKey: MarketKey,
): void {
  let splitStart = trade.start;
  while (splitStart < trade.end) {
    const splitEnd = new Date(splitStart);
    splitEnd.setMinutes(splitStart.getMinutes() < 30 ? 30 : 60, 0, 0);

    if (splitEnd > trade.end) {
      splitEnd.setTime(trade.end.getTime());
    }

    let splitItem: IChartTrade = trade;

    if (marketKey === MarketKey.Wholesale) {
      splitItem = {
        ...trade,
        start: splitStart,
        end: splitEnd,
        base: { start: splitStart, end: splitEnd },
      };
    }

    if (marketKey === MarketKey.BalancingMechanism) {
      splitItem = {
        ...trade,
        start: splitStart,
        end: splitEnd,
        base: { start: trade.start, end: trade.end },
      };
    }

    result.push(splitItem);
    splitStart = splitEnd;
  }
}

function reshapeAndSplitTrades(
  tradesData: IChartTrade[],
  marketKey: MarketKey,
) {
  const result: IChartTrade[] = [];

  for (const item of tradesData) {
    const start = item.start;
    const end = item.end;

    if (tradeFitsOneSettlementPeriod(start, end)) {
      result.push(item);
    } else {
      splitTrade(item, result, marketKey);
    }
  }

  return result;
}
